/**
 * Tela de entrada do link de cerimônia (I2): resolve o `?c=` contra a API e, dando certo,
 * entrega o fluxo para a `SignPage` que já existe. Nenhuma reescrita do app — o encaixe é este
 * componente e mais nada.
 */
import { useEffect, useRef, useState } from 'react';
import { BrandMark } from '@argink/ui';
import { SignPage } from './SignPage';
import { StateCard } from '../components/StateCard';
import { setSessionToken } from '../lib/api';
import {
  replaceCeremonyLinkInUrl,
  resolveCeremonyLink,
  type CeremonyLinkResolution,
} from '../lib/ceremonyLink';

type Phase = { kind: 'resolving' } | { kind: 'resolved'; resolution: CeremonyLinkResolution };

export interface CeremonyEntryPageProps {
  c: string;
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="page">
      <header className="page__header">
        <BrandMark variant="wordmark" className="page__brand" />
        <h1 className="page__title">{title}</h1>
      </header>
      {children}
    </main>
  );
}

export function CeremonyEntryPage({ c }: CeremonyEntryPageProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'resolving' });
  // O `c` é de uso único: em StrictMode o efeito roda duas vezes em desenvolvimento, e sem esta
  // trava a segunda execução gastaria uma resolução à toa. (O servidor sobrevive a isso pela
  // janela de reabertura, mas queimar link por detalhe de renderização seria bobagem.)
  //
  // A trava guarda a PROMESSA, não um booleano de "já comecei". A versão anterior combinava o ref
  // com um `alive` de cleanup e as duas guardas se anulavam: sob StrictMode o efeito rodava, o
  // cleanup marcava `alive = false`, a segunda execução saía no `return` do ref — e a resposta da
  // primeira era descartada por um `alive` que ninguém mais reativava. A tela ficava presa em
  // "Verificando seu link" para sempre, sem erro no console. Guardando a promessa, a segunda
  // execução se pendura na MESMA resolução em vez de disparar outra ou desistir.
  const pending = useRef<Promise<CeremonyLinkResolution> | null>(null);

  useEffect(() => {
    let alive = true;
    pending.current ??= resolveCeremonyLink(c);
    void pending.current.then((resolution) => {
      if (!alive) return;
      if (resolution.status === 'ready') {
        // SST só em memória (contrato 1 do ledger da Rodada 3B) — nunca na URL, nunca em storage.
        setSessionToken(resolution.token);
        // A URL passa a carregar o SUCESSOR do link. Recarregar a página refaz o resolve contra
        // o servidor; nada é ressuscitado do lado do cliente.
        replaceCeremonyLinkInUrl(resolution.nextLink);
      }
      setPhase({ kind: 'resolved', resolution });
    });
    return () => {
      alive = false;
    };
  }, [c]);

  if (phase.kind === 'resolving') {
    return (
      <Shell title="Abrindo sua cerimônia de assinatura">
        <StateCard
          tone="info"
          title="Verificando seu link"
          detail="Estamos confirmando o link que você recebeu por e-mail. Isso leva alguns segundos."
        />
      </Shell>
    );
  }

  const { resolution } = phase;

  if (resolution.status === 'ready') {
    return <SignPage signerId={resolution.signerId} envelopeId={resolution.envelopeId} />;
  }

  if (resolution.status === 'stage_locked') {
    return (
      <Shell title="Sua vez ainda não chegou">
        <StateCard
          tone="info"
          title="Ainda há assinaturas pendentes antes da sua"
          detail="Este documento é assinado por etapas. Assim que a etapa anterior for concluída, você recebe um novo aviso por e-mail com o link para assinar."
        />
      </Shell>
    );
  }

  if (resolution.status === 'rate_limited') {
    return (
      <Shell title="Muitas tentativas seguidas">
        <StateCard
          tone="error"
          title="Aguarde um minuto e abra o link de novo"
          detail="Recebemos tentativas demais deste link em pouco tempo. Espere cerca de um minuto e clique novamente no endereço que você recebeu por e-mail."
        />
      </Shell>
    );
  }

  if (resolution.status === 'unavailable') {
    return (
      <Shell title="Não conseguimos abrir a cerimônia agora">
        <StateCard
          tone="error"
          title="O serviço de assinatura está indisponível no momento"
          detail="Não foi possível confirmar seu link. Tente novamente em alguns minutos; se continuar assim, avise quem enviou o documento."
        />
      </Shell>
    );
  }

  // Recusa genérica — de propósito. O servidor devolve a MESMA resposta para link inexistente,
  // vencido, já usado, substituído por um reenvio e cerimônia encerrada; a tela não inventa uma
  // explicação que o backend não deu (e que revelaria a existência do documento a quem não deve).
  return (
    <Shell title="Este link não está mais válido">
      <StateCard
        tone="error"
        title="Não conseguimos abrir um documento com este link"
        detail="O link pode já ter sido usado, ter vencido ou ter sido substituído por um mais recente. Procure o e-mail mais novo que você recebeu — ou peça a quem enviou o documento para reenviar o convite."
      />
    </Shell>
  );
}
